import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Auth } from "@angular/fire/auth";
import { ActivatedRoute, Router } from '@angular/router';
import { MESSAGE, STORAGE_MODAL_BENEFICIOS_REFORMA_TRAMITE_PENSION_RESPONSE, PENSION_PROCESS_RESPONSE } from "@constants/tramite-pension-constant";
import { REQUEST_ERROR, ROUTING_TRAMITE_PENSION } from "@constants/url.constant";
import { ValidarCuentasDigitalesResponse, ValidarRequest } from "@interfaces/validar-cuentas.interface";
import { Beneficiary, RegistraBeneficiario } from "@shared/common/interfaces/beneficiario.interface";
import { BonoReconocimiento } from "@shared/common/interfaces/bono-reconocimiento.interface";
import { FormaPago } from "@shared/common/interfaces/forma-pago.interface";
import { PersonalData } from "@shared/common/interfaces/personal.interface";
import { IngresarTramitePension } from "@shared/common/interfaces/tramite-pension.interface";
import {
  ConsultarSolicitudTramitePensionRequest,
  PensionTramiteV2Request,
} from "@shared/common/interfaces/tramite-pension-v2.interface";
import { Util } from "@shared/common/utils/util";
import { IValidateCivilStatus } from "@shared/interfaces/validate-civil-status.interface";
import { firstValueFrom, Observable, throwError } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";
import { environment } from 'src/environments/environment';
import { storageTramitePension } from "../pages/personal-data/personal-data.const";
import { ModalBeneficioReforma } from "@shared/common/interfaces/modal-beneficios-reforma-tramite-pension.interface";
import { StorageService } from "@services/storage/storage.service";

@Injectable({
  providedIn: "root",
})
export class TramitePensionService {

  urlBase = environment.BASE_BACKEND + '/tramite-pension';
  private beneficiaries$: Observable<Beneficiary[]> | null = null;
  private personalData$: Observable<PersonalData> | null = null;
  private formaDePagos$: Observable<{ bancos: any[], cuentas: any[], metodoPago: any[] }> | null = null;
  private readonly STORAGE_KEY = 'tipoError';
  public isHomeInitializing: boolean = false;

  constructor(
    public firebaseAuth: Auth,
    private readonly http: HttpClient,
    private readonly util: Util,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute
  ) { }


  public async uid(): Promise<string> {
    try {
      const user = await this.util.getUser();
      if (user) {
        const currentUser: any = await this.firebaseAuth.currentUser;
        return currentUser.uid;
      }
      return "";
    } catch {
      return "";
    }
  }

  getPersonalData(): Observable<PersonalData> {
    if (!this.personalData$) {
      this.personalData$ = this.http.get<PersonalData>(`${this.urlBase}/client-info`)
        .pipe(
          shareReplay(1),
          catchError(this.handleError.bind(this))
        );
    }
    return this.personalData$;
  }

  getBeneficiaries(): Observable<Beneficiary[]> {
    if (!this.beneficiaries$) {
      this.beneficiaries$ = this.http.get<Beneficiary[]>(`${this.urlBase}/beneficiaries`)
        .pipe(
          shareReplay(1),
          catchError(this.handleError.bind(this))
        );
    }
    return this.beneficiaries$;
  }

  uploadDocument(payload: any) {
    const url = `${this.urlBase}/validate-document`;
    return this.http.post(url, payload);
  }

  getDocumentStatus(documentType: string, id: string) {
    const url = `${this.urlBase}/document/${documentType}/${id}`;
    return this.http.get(url);
  }

  postTramitePension(request: IngresarTramitePension) {
    const url = `${this.urlBase}/solicitud`;
    return this.http.post(url, request).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  postTramitePensionV2(request: PensionTramiteV2Request) {
    const url = `${this.urlBase}/v2/solicitud`;
    return this.http.post(url, request).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  postBeneficiariosTramitePension(request: RegistraBeneficiario) {
    const url = `${this.urlBase}/beneficiarios`;
    return this.http.post(url, request).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  postFormaPagoTramitePension(request: FormaPago) {
    const url = `${this.urlBase}/forma-pago`;
    return this.http.post(url, request).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  private handleError(error: any): Observable<never> {
    const errorCode = error.status;
    let tipoError: any;

    if (errorCode === 500 || errorCode === 404) {
      tipoError = errorCode === 500 ? MESSAGE.ERROR_500 : MESSAGE.ERROR_404;

      this.router.navigate([`/${ROUTING_TRAMITE_PENSION.BASE_URL}/${REQUEST_ERROR}`], {
        relativeTo: this.activatedRoute,
        state: { tipoError },
      });
    }

    return throwError(() => error);
  }

  clearCache(): void {
    this.beneficiaries$ = null;
    this.personalData$ = null;
  }

  createPdf(data: any) {
    const url = `${environment.BASE_BACKEND}/client/generate-pdf`;
    const encrypData = { data: this.util.encrypt(JSON.stringify(data)) };
    return this.http.post(url, encrypData, { responseType: 'text' });
  }


  public validarCuentasDigitales(request: ValidarRequest, token: string): Observable<ValidarCuentasDigitalesResponse> {
    let url = `${environment.BASE_BACKEND}/client/validar-cuentas-digitales`;
    let headers = new HttpHeaders().append('Authorization', `Bearer ${token}`);

    let body = request
    return this.http.post(url, body, { headers }) as Observable<ValidarCuentasDigitalesResponse>;
  }

  public obtenerViaDePagos(): Observable<{ bancos: any[], cuentas: any[], metodoPago: any[] }> {
    if (!this.formaDePagos$) {
      this.formaDePagos$ = this.http.get<any>(`${environment.BASE_BACKEND}/bancos/info-config-tramite`)
        .pipe(
          map(response => response.data),
          shareReplay(1),
          catchError(this.handleError.bind(this))
        );
    }
    return this.formaDePagos$;
  }

  public async obtenerViaDePagosPromise(): Promise<{ bancos: any[], cuentas: any[], metodoPago: any[] }> {
    return firstValueFrom(this.obtenerViaDePagos());
  }

  public obtenerEstadoTramitePension(update?: boolean): Observable<any> {
    let url = `${environment.BASE_BACKEND_BENEFICIARIO}/client/pension-tramite`;
    return this.http.post(url, null) as Observable<any>;
  }

  async getCacheEstadoTramite(storageService: StorageService): Promise<any> {
    const pensionProcessResponse = await storageService.get(PENSION_PROCESS_RESPONSE);
    if (pensionProcessResponse?.PensionesEnTramite) {
      return pensionProcessResponse;
    }
    return await firstValueFrom(this.obtenerEstadoTramitePension());
  }

  public obtenerValidacionesTramitePension(tipoTramite: string): Promise<any> {
    let url = `${environment.BASE_BACKEND}/tramite-pension/v2/validaciones`;
    return firstValueFrom(this.http.post(url, { tipoTramite }) as Observable<any>);
  }

  public consultarSolicitudTramitePension(): Promise<any> {
    const url = `${environment.BASE_BACKEND}/tramite-pension/v2/consultar-solicitud`;
    const body: ConsultarSolicitudTramitePensionRequest = {
      numeroTramite: "0",
      fechaDesde: this.util.formattedOneYearAgoDDMMYYYY(),
      fechaHasta: this.util.formattedTodayDDMMYYYY(),
    };
    return firstValueFrom(this.http.post(url, body) as Observable<any>);
  }

  async getInstitucionesDeSalud() {
    if (sessionStorage.getItem(storageTramitePension.institucionesSalud)) {
      const institucionesSaludStorage = sessionStorage.getItem(storageTramitePension.institucionesSalud);
      return institucionesSaludStorage ? JSON.parse(institucionesSaludStorage) : null;
    }
    const institucionesSalud = (await this.http.get<any>(`${this.urlBase}/listado-instituciones-salud`).toPromise()).data.map((institucion: any) => {
      return { value: institucion.code, name: institucion.name };
    });
    sessionStorage.setItem(storageTramitePension.institucionesSalud, JSON.stringify(institucionesSalud));
    return institucionesSalud;

  }

  public async obtenerSaldoCongelado(): Promise<any> {
    const rut = await this.uid();
    const url = `${environment.BASE_BACKEND_NUEVA_WEB}clientes/${rut}/clienteTieneSaldoCongelado`;
    return this.http.get(url).toPromise();
  }

  async validaBonoReconocimiento(request: BonoReconocimiento) {
    const url = `${this.urlBase}/bono-reconocimiento-valido-tramite-pension`;
    return this.http.post(url, request).toPromise();
  }

  validateCivilStatus(): Observable<IValidateCivilStatus> {
    const url = `${this.urlBase}/validar-estado-civil`;
    return this.http.post<IValidateCivilStatus>(url, {})
  }

  setError(error: any) {
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(error));
  }

  getError(): any {
    const data = sessionStorage.getItem(this.STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  }

  clearError() {
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  async getModalBeneficiosReforma(): Promise<ModalBeneficioReforma> {
    const url = `${this.urlBase}/modal-beneficios-reforma`;
    return await firstValueFrom(this.http.get<ModalBeneficioReforma>(url));
  }

  async getCacheModalBeneficiosReforma(storageService: StorageService): Promise<ModalBeneficioReforma> {
    let modal = await storageService.get(STORAGE_MODAL_BENEFICIOS_REFORMA_TRAMITE_PENSION_RESPONSE);
    if (modal) {
      return modal;
    }
    modal = await this.getModalBeneficiosReforma();
    await storageService.set(STORAGE_MODAL_BENEFICIOS_REFORMA_TRAMITE_PENSION_RESPONSE, modal);
    return modal;
  }
}
